import React, { useState, useEffect } from "react";
import AppBreadcrumbs from "../components/ui/AppBreadcrumbs";
import Main from "../pages/Main";
import { useParams } from "react-router-dom";
import { AddmoneyCreditCard, fetchFinkedaGateways } from "../api/addmoneyApi";
import { useWallet } from "../context/walletContext";
import { useNotify } from "../context/NotifyContext";
import CustomerLookup from "../components/CustomerLookup";

const AddmoneyForm = () => {
  const { rule } = useParams();
  const serviceType = "FINKEDA_PG";
  const { refreshWalletCart } = useWallet();
  const { notify } = useNotify();

  const [min, max] = rule?.split("-") || [200, 90000];
  const [loading, setLoading] = useState(false);
  const [gateways, setGateways] = useState([]);
  const [pgType, setPgType] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    settlementType: "Instant Settlement",
    mobile: "",
    amount: "",
    cardType: "CREDITCARD",
    cardNetwork: "MASTERCARD",
    binValue: "",
    markup: "0",
    transactionMode: "All",
    pincode: "",
    areaName: "",
    districtName: "",
    stateName: "",
    remarks: "",
    address: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      const res = await fetchFinkedaGateways();
      const list = Array.isArray(res?.data) ? res.data : res?.data?.gateways || [];
      setGateways(list);
      const first =
        list?.[0]?.pgType || list?.[0]?.name || list?.[0]?.gatewayType || "";
      if (first) setPgType(first);
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (["amount", "mobile", "pincode", "binValue"].includes(name)) {
      newValue = value.replace(/\D/g, "");
    }
    if (name === "binValue") {
      newValue = newValue.slice(0, 6);
    }
    if (name === "markup") {
      newValue = value.replace(/[^\d.]/g, "");
    }

    setForm({ ...form, [name]: newValue });

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }

    if (name === "amount") {
      const numValue = Number(newValue);
      if (numValue && (numValue < min || numValue > max)) {
        setErrors({
          ...errors,
          amount: `Amount must be between ₹${min} and ₹${max}`,
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let tempErrors = {};
    Object.keys(form).forEach((key) => {
      if (["binValue", "markup"].includes(key)) return;
      if (!form[key]?.toString().trim()) {
        tempErrors[key] = "This field is required";
      }
    });

    const numValue = Number(form.amount);
    if (isNaN(numValue) || numValue < Number(min) || numValue > Number(max)) {
      tempErrors.amount = `Amount must be between ₹${min} and ₹${max}`;
    }

    if (!Number.isInteger(numValue)) {
      tempErrors.amount = "Amount must be a whole number (no decimals)";
    }

    if (!pgType && gateways.length) {
      tempErrors.pgType = "Select a payment gateway";
    }

    if (form.binValue && String(form.binValue).length !== 6) {
      tempErrors.binValue = "BIN must be the first 6 digits of the card";
    }
    if (form.markup !== "" && (Number(form.markup) < 0 || Number(form.markup) > 100)) {
      tempErrors.markup = "Markup must be a percentage between 0 and 100";
    }

    if (Object.keys(tempErrors).length > 0) {
      setErrors(tempErrors);
      notify("Please fix the errors in the form.", "error");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: form.name,
        mobile: form.mobile,
        email: form.email,
        pincode: form.pincode,
        areaName: form.areaName,
        districtName: form.districtName,
        stateName: form.stateName,
        address: form.address,
        amount: numValue,
        remarks: form.remarks,
        serviceType,
        settlementType: form.settlementType,
        pgType: pgType || undefined,
        settledIn: form.settlementType?.includes("T+1") ? "counter" : "super",
        cardType: form.cardType,
        cardNetwork: form.cardNetwork,
        markup: form.markup || "0",
        binValue: form.binValue || undefined,
      };

      const result = await AddmoneyCreditCard(payload);

      if (result?.success) {
        if (result.requiresKyc && result.kycUrl) {
          notify(
            "Complete KYC using the link, then retry checkout for this PAN.",
            "success"
          );
          window.location.href = result.kycUrl;
          return;
        }
        notify(result?.message || "Redirecting to Finkeda payment…", "success");
        const url = result.redirectionUrl;
        if (url) {
          window.location.href = url;
          return;
        }
        refreshWalletCart();
      } else {
        notify(result?.message || "Failed to generate payment link", "error");
      }
    } catch (err) {
      notify("Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Main showRightPanel={false}>
      <div className="flex flex-col">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/" },
            { label: "MATM Module" },
            { label: serviceType },
            { label: "Initiate Transaction" },
          ]}
        />

        <div className="ds-card p-4 sm:p-6 w-full max-w-4xl mb-4 mt-4">
          <h2 className="text-[15px] sm:text-lg font-semibold text-[var(--text-dark)] mb-3">
            Load Money Details (<span className="text-[var(--error-color)]">*</span> Are Required Fields)
          </h2>
          <p className="text-sm mb-4">
            <span className="font-bold text-[var(--success-color)]">
              You can add between ₹{min} and ₹{max} in a single transaction.
            </span>
          </p>

          <CustomerLookup
            onSelect={(c) => {
              setForm((prev) => ({
                ...prev,
                name: c.name || prev.name,
                mobile: c.mobile || prev.mobile,
                email: c.email || prev.email,
                pincode: c.pincode || prev.pincode,
                areaName: c.areaName || prev.areaName,
                districtName: c.districtName || prev.districtName,
                stateName: c.stateName || prev.stateName,
                address: c.address || prev.address,
              }));
              setErrors({});
            }}
            onNewCustomer={() => {
              setForm((prev) => ({
                ...prev,
                name: "",
                mobile: "",
                email: "",
                pincode: "",
                areaName: "",
                districtName: "",
                stateName: "",
                address: "",
              }));
            }}
          />

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InputField label="Customer's Full Name" required name="name" value={form.name} onChange={handleChange} error={errors.name} />
              <SelectField label="Select Settlement Type" name="settlementType" value={form.settlementType} required options={["Instant Settlement", "(T+1 Settlement)"]} onChange={handleChange} error={errors.settlementType} />
              <InputField label="Email" required name="email" value={form.email} onChange={handleChange} error={errors.email} />
              <InputField label="Mobile Number" required name="mobile" value={form.mobile} onChange={handleChange} error={errors.mobile} />
              <InputField label="Transaction Amount" required name="amount" value={form.amount} onChange={handleChange} error={errors.amount} />
              <SelectField
                label="Card Type"
                required
                name="cardType"
                value={form.cardType}
                options={["CREDITCARD"]}
                onChange={handleChange}
                error={errors.cardType}
              />
              <SelectField
                label="Card Network"
                required
                name="cardNetwork"
                value={form.cardNetwork}
              options={["RUPAYCARD", "VISACARD", "MASTERCARD"]}
                onChange={handleChange}
                error={errors.cardNetwork}
              />
              <InputField
                label="BIN (first 6 digits, optional)"
                name="binValue"
                value={form.binValue}
                onChange={handleChange}
                error={errors.binValue}
                maxLength={6}
              />
              <InputField
                label="Markup % (stays in merchant wallet)"
                name="markup"
                value={form.markup}
                onChange={handleChange}
                error={errors.markup}
              />
              <div>
                <label className="ds-label">
                  Payment Gateway <span className="text-[var(--error-color)]">*</span>
                </label>
                <select
                  value={pgType}
                  onChange={(e) => setPgType(e.target.value)}
                  className={`ds-input ${errors.pgType ? "border-[var(--error-color)]" : ""}`}
                >
                  {!gateways.length && <option value="">Auto (server default)</option>}
                  {gateways.map((g, i) => {
                    const val = g.pgType || g.name || g.gatewayType || String(g);
                    return (
                      <option key={i} value={val}>
                        {g.name || g.pgType || val}
                      </option>
                    );
                  })}
                </select>
                {errors.pgType && (
                  <p className="text-[var(--error-color)] text-sm mt-1">{errors.pgType}</p>
                )}
              </div>
              <SelectField label="Transaction Mode" required name="transactionMode" value={form.transactionMode} options={["All"]} onChange={handleChange} error={errors.transactionMode} />
              <InputField label="Area Pin Code" name="pincode" value={form.pincode} onChange={handleChange} error={errors.pincode} />
              <InputField label="Area Name" name="areaName" value={form.areaName} onChange={handleChange} error={errors.areaName} />
              <InputField label="District Name" name="districtName" value={form.districtName} onChange={handleChange} error={errors.districtName} />
              <InputField label="State Name" name="stateName" value={form.stateName} onChange={handleChange} error={errors.stateName} />
              <TextAreaField label="Customer Address" name="address" value={form.address} onChange={handleChange} error={errors.address} />
              <TextAreaField label="Remarks" required name="remarks" value={form.remarks} onChange={handleChange} error={errors.remarks} />
            </div>

            <button
              type="submit"
              disabled={!form?.amount || loading}
              className="ds-btn ds-btn-primary mt-4"
            >
              {loading ? "Processing..." : "Generate Payment Link"}
            </button>
          </form>
        </div>
      </div>
    </Main>
  );
};

const InputField = ({ label, error, ...props }) => (
  <div>
    <label className="ds-label">
      {label} {props.required && <span className="text-[var(--error-color)]">*</span>}
    </label>
    <input
      {...props}
      className={`ds-input ${error ? "border-[var(--error-color)]" : ""}`}
    />
    {error && <p className="text-[var(--error-color)] text-sm mt-1">{error}</p>}
  </div>
);

const TextAreaField = ({ label, error, ...props }) => (
  <div>
    <label className="ds-label">
      {label} {props.required && <span className="text-[var(--error-color)]">*</span>}
    </label>
    <textarea
      rows="3"
      {...props}
      className={`ds-input resize-none ${error ? "border-[var(--error-color)]" : ""}`}
    />
    {error && <p className="text-[var(--error-color)] text-sm mt-1">{error}</p>}
  </div>
);

const SelectField = ({ label, options, error, ...props }) => (
  <div>
    <label className="ds-label">
      {label} {props.required && <span className="text-[var(--error-color)]">*</span>}
    </label>
    <select
      {...props}
      className={`ds-input ${error ? "border-[var(--error-color)]" : ""}`}
    >
      {options.map((o, i) => (
        <option key={i}>{o}</option>
      ))}
    </select>
    {error && <p className="text-[var(--error-color)] text-sm mt-1">{error}</p>}
  </div>
);

export default AddmoneyForm;
